import type { SystemShip } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { payloadClient } from '.';

type PaginatedDocs<T = any> = {
  docs: T[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage?: null | number | undefined;
  page?: number;
  pagingCounter: number;
  prevPage?: null | number | undefined;
  totalDocs: number;
  totalPages: number;
};

export type SystemShipOverviewRow = {
  id: string;
  name: string;
  imo?: string | null;
  flag?: string | null;
  callSign?: string | null;
  mmsi?: string | null;
  archived: boolean;
  active: boolean;
  currentCustomer?: { id: string | null; name: string | null } | null;
  objectCount: number;
};

export type SystemShipDetailResponse = {
  systemShip: SystemShip;
  activeObject: {
    id: string;
    name?: string | null;
    description?: string | null;
    activePeriods?: Array<{ startDate?: string | null; endDate?: string | null }> | null;
    assignedCustomer?: { id?: string; name?: string | null } | string | null;
    assignedHelicopterServiceProvider?: { id?: string; name?: string | null } | string | null;
    linkedWindpark?: { id?: string; name?: string | null } | string | null;
    categoryMetadataValues?: Record<string, unknown> | null;
    updatedAt?: string | null;
  } | null;
  historicalObjects: Array<{
    id: string;
    name?: string | null;
    description?: string | null;
    activePeriods?: Array<{ startDate?: string | null; endDate?: string | null }> | null;
    assignedCustomer?: { id?: string; name?: string | null } | string | null;
    assignedHelicopterServiceProvider?: { id?: string; name?: string | null } | string | null;
    linkedWindpark?: { id?: string; name?: string | null } | string | null;
    categoryMetadataValues?: Record<string, unknown> | null;
    updatedAt?: string | null;
  }>;
  occupiedPeriods: Array<{
    startDate: string;
    endDate: string;
    objectId: string | null;
    visible: boolean;
    active: boolean;
  }>;
  today: string;
};

export const systemShipKeys = {
  all: ['system-ships'] as const,
  detailDoc: (id: string) => [...systemShipKeys.all, 'doc', id] as const,
  overview: (params: {
    page: number;
    limit: number;
    searchTerm?: string;
    overviewStatus?: 'active' | 'inactive' | 'archived';
  }) => [...systemShipKeys.all, 'overview', params] as const,
  detail: (id: string) => [...systemShipKeys.all, 'detail', id] as const,
  suggestions: (query: string) => [...systemShipKeys.all, 'suggestions', query] as const,
} as const;

export const useSystemShipsOverview = (params: {
  page: number;
  limit: number;
  searchTerm?: string;
  overviewStatus?: 'active' | 'inactive' | 'archived';
  disabled?: boolean;
}) =>
  useQuery({
    queryKey: systemShipKeys.overview(params),
    enabled: params.disabled !== true,
    placeholderData: (previousData) => previousData,
    queryFn: () =>
      payloadClient.customRequest<PaginatedDocs<SystemShipOverviewRow>>({
        subpath: '/system-ships/overview',
        method: 'POST',
        data: {
          page: params.page,
          limit: params.limit,
          searchTerm: params.searchTerm,
          overviewStatus: params.overviewStatus,
        },
      }),
  });

export const useSystemShip = (id: string, params?: { disabled?: boolean }) =>
  useQuery({
    queryKey: systemShipKeys.detailDoc(id),
    enabled: !params?.disabled && Boolean(id),
    queryFn: () =>
      payloadClient.findById({
        collection: 'system-ships',
        id,
      }),
  });

export const useSystemShipDetail = (id: string, params?: { disabled?: boolean }) =>
  useQuery({
    queryKey: systemShipKeys.detail(id),
    enabled: !params?.disabled && Boolean(id),
    queryFn: () =>
      payloadClient.customRequest<SystemShipDetailResponse>({
        subpath: `/system-ships/${id}/detail`,
        method: 'GET',
      }),
  });

export const useSystemShipSuggestions = (query: string, params?: { disabled?: boolean }) =>
  useQuery({
    queryKey: systemShipKeys.suggestions(query),
    enabled: !params?.disabled && query.trim().length >= 2,
    placeholderData: (previousData) => previousData,
    queryFn: () =>
      payloadClient.customRequest<{ docs: SystemShip[] }>({
        subpath: `/system-ships/suggestions?q=${encodeURIComponent(query.trim())}`,
        method: 'GET',
      }),
  });

export const useCreateSystemShip = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Pick<SystemShip, 'name' | 'imo' | 'flag' | 'callSign' | 'mmsi'>) => {
      const response = await payloadClient.create({
        collection: 'system-ships',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemShipKeys.all });
    },
  });
};

export const useUpdateSystemShip = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<SystemShip, 'name' | 'imo' | 'flag' | 'callSign' | 'mmsi' | 'archived'>>;
    }) => {
      const response = await payloadClient.updateById({
        collection: 'system-ships',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: systemShipKeys.all });
      queryClient.invalidateQueries({ queryKey: systemShipKeys.detailDoc(id) });
      queryClient.invalidateQueries({ queryKey: systemShipKeys.detail(id) });
    },
  });
};
